import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  Wallet,
  TrendingUp,
  Lock,
  Users,
  ArrowRight,
  CheckCircle,
  BarChart,
  Globe,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const Home: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const heroTexts = [
    {
      main: "Protect Your Crypto",
      highlight: "From Market Downturns",
      color: "text-primary",
    },
    {
      main: "Recover Lost Assets",
      highlight: "From Scams & Hacks",
      color: "text-blue-500",
    },
    {
      main: "Secure Your Wealth",
      highlight: "With Smart Protection",
      color: "text-green-500",
    },
  ];

  // Rotating text effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setFade(true);
      }, 1500);
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out-quad",
      once: false,
    });
  }, []);

  // Intersection observer for stats
  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/" />

      {/* Hero Section */}
      <section className="container mx-auto px-5 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right" data-aos-delay="100">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Shield className="h-4 w-4 mr-2" />
              Secure Asset Protection & Recovery
            </div>

            {/* Rotating Hero Text */}
            <div className="h-48 lg:h-40 mb-6">
              <div
                className={`transition-all duration-500 ${
                  fade
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-4"
                }`}
              >
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                  {heroTexts[currentTextIndex].main}
                  <span
                    className={`block ${heroTexts[currentTextIndex].color}`}
                  >
                    {heroTexts[currentTextIndex].highlight}
                  </span>
                </h1>
              </div>
            </div>

            <p
              className="text-xl text-gray-600 dark:text-gray-400 mb-10 max-md:pt-10"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              ObeliskRecovery combines intelligent wallet connection technology
              with expert recovery services to secure your assets when markets
              get turbulent.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <Link
                to="/register"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} text-center`}
              >
                Start Protecting Assets
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
              <Link
                to="/recovery"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonLarge} text-center`}
              >
                <Shield className="h-5 w-5 mr-2" />
                Need Recovery Help?
              </Link>
            </div>
          </div>
          <div className="relative" data-aos="fade-left" data-aos-delay="400">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="space-y-6">
                <div
                  className="flex items-center space-x-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl"
                  data-aos="zoom-in"
                  data-aos-delay="100"
                >
                  <TrendingUp className="h-8 w-8 text-green-500" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      Market Protection
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Auto-secure assets when markets drop
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl"
                  data-aos="zoom-in"
                  data-aos-delay="200"
                >
                  <Shield className="h-8 w-8 text-blue-500" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      Recovery Services
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Expert fund recovery from hacks & scams
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-center space-x-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl"
                  data-aos="zoom-in"
                  data-aos-delay="300"
                >
                  <Wallet className="h-8 w-8 text-purple-500" />
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      Multi-Chain Support
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Support for Bitcoin, Ethereum, Solana & more
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900/50">
        <div className="container mx-auto px-5">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              How We Protect Your Assets
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Our comprehensive solution combines prevention and recovery to
              keep your crypto safe
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 hover:border-primary transition-colors"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                <Wallet className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Smart Wallet Connection
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Connect your wallets securely with our encrypted connection
                system that monitors market conditions and protects your assets
                during downturns.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Real-time market monitoring</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Automated protection triggers</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Multi-wallet support</span>
                </li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 hover:border-primary transition-colors"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6">
                <BarChart className="h-7 w-7 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Market Downturn Protection
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                When markets show signs of significant decline, our system
                automatically initiates protection protocols to secure your
                assets from substantial losses.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>AI-powered market analysis</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Custom protection thresholds</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Instant notifications</span>
                </li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 hover:border-primary transition-colors"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="w-14 h-14 bg-green-500/10 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Expert Crypto Recovery
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                If you've fallen victim to scams, hacks, or lost access to
                funds, our expert recovery team works to retrieve your assets
                with proven success rates.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Scam & hack recovery</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Wallet access recovery</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Multi-network support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20" ref={statsRef}>
        <div className="container mx-auto px-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="100"
            >
              <div className="text-5xl font-bold text-primary mb-2">
                {statsInView && <CountUp end={85} suffix="%" duration={2} />}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Assets Protected During Downturns
              </div>
            </div>
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <div className="text-5xl font-bold text-green-500 mb-2">
                {statsInView && (
                  <CountUp end={42} prefix="$" suffix="M+" duration={2.5} />
                )}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Funds Successfully Recovered
              </div>
            </div>
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="300"
            >
              <div className="text-5xl font-bold text-blue-500 mb-2">
                {statsInView && <CountUp end={5000} suffix="+" duration={2} />}
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Protected Wallets
              </div>
            </div>
            <div
              className="text-center"
              data-aos="zoom-in"
              data-aos-delay="400"
            >
              <div className="text-5xl font-bold text-purple-500 mb-2">
                24/7
              </div>
              <div className="text-gray-600 dark:text-gray-400">
                Monitoring & Support
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 bg-gradient-to-r from-primary to-purple-600"
        data-aos="fade-up"
      >
        <div className="container mx-auto px-5 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Secure Your Crypto Assets?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join thousands of investors who trust ObeliskRecovery to protect
            their assets and recover lost funds
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <Link
              to="/register"
              className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-xl font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              Start Free Protection
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <Link
              to="/about"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-xl font-bold text-lg transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="ObeliskRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  ObeliskRecovery
                </span>
              </div>
              <p className="mb-4">
                Secure your crypto assets against market downturns and recover
                lost funds with expert assistance.
              </p>
              <div className="flex space-x-4">
                <Globe className="h-5 w-5 hover:text-white cursor-pointer" />
                <Users className="h-5 w-5 hover:text-white cursor-pointer" />
                <Lock className="h-5 w-5 hover:text-white cursor-pointer" />
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/wallet" className="hover:text-white">
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link to="/recovery" className="hover:text-white">
                    Fund Recovery
                  </Link>
                </li>
                <li>
                  <Link to="/dashboard" className="hover:text-white">
                    Dashboard
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="hover:text-white">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2">support@obeliskrecovery.com</p>
              <p>24/7 Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p>
              © {new Date().getFullYear()} ObeliskRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
